import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Popover,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import { showSystemNotification } from '../../utils/systemNotification';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AssignmentIcon from '@mui/icons-material/Assignment';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EscalatorWarningIcon from '@mui/icons-material/EscalatorWarning';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ReplayIcon from '@mui/icons-material/Replay';
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import {
  fetchNotifications,
  markNotificationsRead,
  prependNotification,
  upsertNotification,
  setUnreadCount,
  selectNotifications,
  selectUnreadCount,
  selectIsLoading,
} from '../../redux/slices/workflowApprovalSlice';

const NOTIFICATION_TYPE_CONFIG = {
  approval_request: { icon: <AssignmentIcon fontSize="small" />, color: '#1976d2' },
  reminder: { icon: <NotificationsActiveIcon fontSize="small" />, color: '#ed6c02' },
  escalation: { icon: <EscalatorWarningIcon fontSize="small" />, color: '#d32f2f' },
  approved: { icon: <CheckCircleIcon fontSize="small" />, color: '#2e7d32' },
  rejected: { icon: <CancelIcon fontSize="small" />, color: '#d32f2f' },
  delegated: { icon: <SwapHorizIcon fontSize="small" />, color: '#9c27b0' },
  returned: { icon: <ReplayIcon fontSize="small" />, color: '#ed6c02' },
  fyi: { icon: <InfoIcon fontSize="small" />, color: '#0288d1' },
};

const CHAT_CONFIG = { icon: <ChatBubbleOutlineIcon fontSize="small" />, color: '#7b1fa2' };

/**
 * Which icon a row gets.
 *
 * Chat is recognised by its `action_url`, NOT by `app_type`. `app_type` is a
 * wire-only field — `workflow_engine.Notification` has no such column — so a
 * row read back from the API after a reload would lose it and the same
 * notification would change its icon under the user. The URL is stored.
 */
function iconConfigFor(notification) {
  if (/\/(projects|portal)\/chat/.test(notification.action_url || '')) {
    return CHAT_CONFIG;
  }
  return NOTIFICATION_TYPE_CONFIG[notification.notification_type]
    || NOTIFICATION_TYPE_CONFIG.fyi;
}

function getTimeAgo(dateString) {
  if (!dateString) return '';
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

/**
 * Is this browser already showing the very conversation the notification is
 * about? Then say nothing: a toast for a message the user is watching arrive
 * is noise, and the message is already on screen.
 *
 * Matches on the query string rather than the path alone because both chat
 * surfaces — the ERP page and the customer portal — address a room as
 * `?project=<id>`.
 */
function isViewingChat(n) {
  if (typeof window === 'undefined') return false;
  if (document.visibilityState !== 'visible') return false;
  const { pathname, search } = window.location;
  if (!/\/(projects|portal)\/chat$/.test(pathname)) return false;
  const current = new URLSearchParams(search).get('project');
  return String(current || '') === String(n.project_id || '');
}

/**
 * A chat toast AUTO-CLOSES, unlike the workflow toast beside it. A workflow
 * approval waits for a decision and so waits for a dismissal; a chat message
 * has already been delivered by the time you read it.
 */
function chatToast(n, navigate) {
  toast.info(
    [n.title, n.message].filter(Boolean).join('\n'),
    {
      toastId: n.id,
      position: 'top-right',
      autoClose: 6000,
      closeOnClick: true,
      onClick: () => n.action_url && navigate(n.action_url),
    },
  );

  // ...and the OS notification beside it. The service worker deliberately
  // stays silent while a window is visible, precisely so this can run: only
  // the page knows which conversation is on screen. `tag` is the room, so
  // several open tabs collapse into one tray entry rather than N.
  showSystemNotification({
    title: n.title,
    body: n.message,
    tag: n.tag,
    url: n.action_url,
    appType: 'chat',
    onClick: () => n.action_url && navigate(n.action_url),
  });
}

export default function NotificationDropdown({ anchorEl, onClose, onAppNotification }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);
  const isLoading = useSelector(selectIsLoading);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectCountRef = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 10;
  const open = Boolean(anchorEl);

  // Fetch notifications when popover opens
  useEffect(() => {
    if (open) {
      dispatch(fetchNotifications({ limit: 15 }));
    }
  }, [open, dispatch]);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    // Prevent duplicate connections (StrictMode, re-renders)
    if (wsRef.current) {
      const state = wsRef.current.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // In dev, React runs on :3000 but Django ASGI (WebSocket) is on :8000
    const wsHost = process.env.REACT_APP_WS_HOST
      || (window.location.port === '3000'
          ? `${window.location.hostname}:8000`
          : window.location.host);
    const token = localStorage.getItem('token');
    const wsUrl = `${protocol}//${wsHost}/ws/workflow/notifications/${token ? `?token=${token}` : ''}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Workflow notification WebSocket connected');
        reconnectCountRef.current = 0; // Reset on successful connection
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_notification') {
            const n = data.notification;
            const appType = n.app_type;

            // Chat goes in the bell like everything else, but it COALESCES:
            // the server keeps one unread row per conversation and updates it
            // in place, so this must replace the entry already in the list
            // rather than stack a second one on top of it.
            if (appType === 'chat') {
              dispatch(upsertNotification(n));
              if (!isViewingChat(n)) chatToast(n, navigate);
              return;
            }

            dispatch(prependNotification(n));

            if ((appType === 'vms' || appType === 'fd') && onAppNotification) {
              // Coloured sticky-note toast handled by ModernMainLayout
              onAppNotification(n);
            } else {
              // Generic workflow toast
              toast.info(
                `${n.title}\n${n.message}`,
                { toastId: n.id, position: 'top-right', autoClose: false, closeOnClick: false },
              );
            }
          } else if (data.type === 'unread_count') {
            dispatch(setUnreadCount(data.count));
          }
        } catch (err) {
          console.error('Error parsing WS message:', err);
        }
      };

      ws.onclose = (event) => {
        console.log('Workflow notification WebSocket closed:', event.code);
        wsRef.current = null;
        // Don't reconnect on: 1000 (normal), 4001 (unauth), 1012 (service restart)
        const noReconnectCodes = [1000, 4001, 1012];
        if (!noReconnectCodes.includes(event.code) && reconnectCountRef.current < MAX_RECONNECT_ATTEMPTS) {
          reconnectCountRef.current += 1;
          // Exponential backoff: 5s, 10s, 20s, 40s... capped at 60s
          const delay = Math.min(5000 * Math.pow(2, reconnectCountRef.current - 1), 60000);
          console.log(`[WS] Reconnecting in ${delay / 1000}s (attempt ${reconnectCountRef.current}/${MAX_RECONNECT_ATTEMPTS})`);
          reconnectTimerRef.current = setTimeout(connectWebSocket, delay);
        } else if (reconnectCountRef.current >= MAX_RECONNECT_ATTEMPTS) {
          console.warn('[WS] Max reconnect attempts reached, stopping.');
        }
      };

      ws.onerror = () => {
        // onclose will fire after this, which handles reconnection
      };
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    connectWebSocket();
    return () => {
      clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connectWebSocket]);

  const handleMarkAllRead = () => {
    dispatch(markNotificationsRead({ markAll: true }));
    // Also tell WS server
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'mark_all_read' }));
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.is_read) {
      dispatch(markNotificationsRead({ notificationIds: [notification.id] }));
    }
    onClose();
    // Navigate to action URL or default to my-approvals
    if (notification.action_url) {
      navigate(notification.action_url);
    } else {
      navigate('/workflow/my-approvals');
    }
  };

  const displayNotifications = (notifications || []).slice(0, 15);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: { width: 380, maxHeight: 520, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>Notifications</Typography>
          {unreadCount > 0 && (
            <Chip label={unreadCount} color="error" size="small" />
          )}
        </Box>
        {unreadCount > 0 && (
          <Button size="small" startIcon={<DoneAllIcon />} onClick={handleMarkAllRead}>
            Mark all read
          </Button>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading && displayNotifications.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : displayNotifications.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <Typography variant="body2">No notifications yet</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {displayNotifications.map((notification, index) => {
              const typeConfig = iconConfigFor(notification);
              return (
                <React.Fragment key={notification.id || index}>
                  <ListItem
                    button
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      px: 2,
                      py: 1,
                      borderLeft: notification.is_read ? 'none' : `3px solid ${typeConfig.color}`,
                      bgcolor: notification.is_read ? 'transparent' : 'action.hover',
                      '&:hover': { bgcolor: 'action.selected' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36, color: typeConfig.color }}>
                      {typeConfig.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={notification.is_read ? 400 : 600} noWrap>
                          {notification.title}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {notification.message}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                            {getTimeAgo(notification.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < displayNotifications.length - 1 && <Divider component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          size="small"
          startIcon={<SettingsIcon />}
          onClick={() => { onClose(); navigate('/workflow/notification-preferences'); }}
        >
          Preferences
        </Button>
        <Button
          size="small"
          onClick={() => { onClose(); navigate('/workflow/my-approvals'); }}
        >
          View All
        </Button>
      </Box>
    </Popover>
  );
}
